import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { forwardRef } from 'react';
import type { V3To } from '../utils/router/v3Bridge';
import styles from './DropdownMenuItem.module.scss';
import Button from './Button';

export type DropdownMenuItemType<Data> = {
  id: string;
  className?: string;
  link?: V3To;
  data: Data;
  hotkeyAction?: string;
};

export type RenderItemFn<Data> = (
  data: Data,
  options: { id: string; focused?: boolean },
) => React.ReactNode;

const KEY_MAP: Record<string, string> = {
  Enter: '⏎',
  Tab: 'Tab',
};

interface HotkeyInfoProps {
  keys: Set<string>;
  action: string | undefined;
}

const HotkeyInfo = ({ keys, action }: HotkeyInfoProps) => {
  return (
    <span className={styles.hotkeyInfo}>
      {Array.from(keys.values()).map((key, i) => {
        return (
          <span key={key}>
            {i > 0 ? ' or ' : null}
            <kbd>{KEY_MAP[key]}</kbd>
          </span>
        );
      })}
      {action ? <span>to {action}</span> : null}
    </span>
  );
};

interface DropdownMenuItemProps<Data>
  extends Pick<HTMLAttributes<HTMLElement>, 'onClick' | 'onMouseEnter'> {
  item: DropdownMenuItemType<Data>;
  focused?: boolean;
  renderItem: RenderItemFn<Data>;
  hotkeysEnabled: boolean;
}

const DropdownMenuItemInner = <Data,>(
  {
    item,
    focused,
    onClick,
    onMouseEnter,
    renderItem,
    hotkeysEnabled,
  }: DropdownMenuItemProps<Data>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const inner = renderItem(item.data, {
    id: item.id,
    focused,
  });

  return (
    <Button
      ref={ref}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={classNames(styles.item, item.className, {
        [styles.focused]: focused,
      })}
      role="menuitem"
      to={item.link}
    >
      {inner}
      {hotkeysEnabled && focused && (
        <HotkeyInfo
          keys={item.link ? new Set(['Enter']) : new Set(['Enter', 'Tab'])}
          action={item.hotkeyAction}
        />
      )}
    </Button>
  );
};

const DropdownMenuItem = forwardRef(DropdownMenuItemInner) as <Data>(
  props: DropdownMenuItemProps<Data> & {
    ref?: React.ForwardedRef<HTMLButtonElement>;
  },
) => ReturnType<typeof DropdownMenuItemInner>;

export default DropdownMenuItem;
